/**
 * WhatsNew page types
 * Type definitions for what's new data and API responses
 */

export interface UseCase {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
}

export enum WhatsNewApprovalStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface WhatsNewUser {
  id: string;
  name: string;
  email: string;
}

export interface WhatsNewItemTranslation {
  id: string;
  whatsNewItemId: string;
  language: string;
  title: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsNewItem {
  id: string;
  icon: string;
  color: string;
  title: string;
  body: string;
  order: number;
  privileges?: string[];
  translations: WhatsNewItemTranslation[];
}

export interface WhatsNewTranslation {
  id: string;
  whatsNewId: string;
  language: string;
  welcomeMessage: string;
  subtitle?: string | null;
  footer?: string | null;
  confirmButtonText?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformVersionSummary {
  id: string;
  version: string;
  name?: string | null;
  isCurrent: boolean;
}

export interface WhatsNewPage {
  id: string;
  name: string;
  slug: string;
}

export interface WhatsNewActionParams {
  redirect_to?: string;
  show_tour?: boolean;
  open_filter?: boolean;
}

export interface WhatsNew {
  id: string;
  platformVersion: PlatformVersionSummary;
  welcomeMessage: string;
  subtitle?: string | null;
  footer?: string | null;
  confirmButtonText?: string | null;
  tenantSlug: string;
  isActive: boolean;
  publishedAt: string;
  items: WhatsNewItem[];
  translations: WhatsNewTranslation[];
  createdAt: string;
  updatedAt: string;
  // Approval workflow fields
  approvalStatus: WhatsNewApprovalStatus;
  createdBy?: WhatsNewUser;
  approvedBy?: WhatsNewUser | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  useCaseSlug?: string | null;
  useCase?: UseCase | null;
  /**
   * Distinct people who have seen this announcement, counted in the same query as the listing.
   *
   * Distinct READERS, not openings — reopening the modal increments a separate column rather than
   * adding a row. There is deliberately no denominator: the BFF has no census of a tenant's users.
   * Naturally `0` for anything not approved, since only approved announcements are delivered.
   *
   * Optional because it only exists from BFF 3.6.0 — against an older deployment the field is
   * simply absent, and the card omits the reach line rather than rendering a wrong `0`.
   */
  viewerCount?: number;
  pageId?: string | null;
  page?: WhatsNewPage | null;
  actionParams?: WhatsNewActionParams | null;
}

export interface WhatsNewPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface WhatsNewResponse {
  data: WhatsNew[];
  pagination: WhatsNewPagination;
}

export interface WhatsNewRequest {
  platformVersionId?: string;
  tenantSlug?: string;
  isActive?: boolean;
  useCaseSlug?: string;
  page?: number;
  limit?: number;
}

export interface WhatsNewErrorResponse {
  message: string;
  statusCode: number;
}

export interface WhatsNewItemUpdateInput {
  icon: string;
  color: string;
  title: string;
  body: string;
  order: number;
  privileges?: string[];
  translations: Array<{
    language: string;
    title: string;
    body: string;
  }>;
}

export interface WhatsNewTranslationInput {
  language: string;
  welcomeMessage: string;
  subtitle?: string;
  footer?: string;
  confirmButtonText?: string;
}

export interface WhatsNewUpdatePayload {
  platformVersionId?: string;
  welcomeMessage?: string;
  subtitle?: string;
  footer?: string;
  confirmButtonText?: string;
  publishedAt?: string;
  items?: WhatsNewItemUpdateInput[];
  translations?: WhatsNewTranslationInput[];
  isActive?: boolean;
  useCaseSlug?: string | null;
  actionParams?: WhatsNewActionParams | null;
}

export interface WhatsNewSingleResponse {
  data: WhatsNew;
}

/** One day of the reach series. */
export interface ReachPoint {
  /** ISO date, `YYYY-MM-DD`. */
  date: string;
  viewers: number;
}

/**
 * Daily distinct readers, from `GET /v1/whats-new/reach-series` (BFF 3.7.0).
 *
 * **`total` and `distinctViewers` are different numbers and must not be mixed.** A day counts a
 * person once, so `total` (the sum of the days, i.e. the area under the curve) double-counts
 * anyone who read on more than one day; `distinctViewers` is the people in the window. Pair
 * `total` with `previousTotal` for the period-over-period change, since both are computed the
 * same way.
 *
 * `byDay` and `previousByDay` have one point per day with gaps filled as 0 and the same length,
 * so the two lines align index for index.
 */
export interface ReachSeries {
  range: { from: string; to: string };
  previousRange: { from: string; to: string };
  byDay: ReachPoint[];
  previousByDay: ReachPoint[];
  total: number;
  previousTotal: number;
  distinctViewers: number;
  previousDistinctViewers: number;
}

/** One person who opened an announcement. */
export interface WhatsNewViewer {
  /**
   * User id from the /my-account backend.
   *
   * It is all the BFF has: platform users live in another service, so there is no name or e-mail
   * to show alongside it.
   */
  platformUserId: number;
  seenAt: string;
  /** Times they opened it. */
  viewCount: number;
  durationMs: number | null;
  itemsSeen: number | null;
}

export interface WhatsNewViewersResponse {
  viewers: WhatsNewViewer[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Reading time across every announcement, from `GET /v1/whats-new/stats/summary`.
 *
 * The **median**, not the mean: `durationMs` measures how long the modal stayed open, so one
 * forgotten tab is an outlier big enough to move an average by itself.
 */
export interface WhatsNewReadingSummary {
  totalViews: number;
  announcementsWithViews: number;
  /** Null when nobody reported one — never 0, which would read as "opened and closed at once". */
  medianDurationMs: number | null;
  /** How many of `totalViews` carried a duration. The median covers these, not all of them. */
  reportedDurationCount: number;
}
